// frontend/src/components/Header.jsx
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { FaBars, FaTimes, FaPhoneAlt, FaEnvelope, FaUserCircle, FaSignOutAlt, FaSignInAlt } from 'react-icons/fa';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import logo from '../assets/logo/mmlogo.png';

axios.defaults.baseURL = process.env.REACT_APP_API_URL || 'https://api.marketmaestroovip.in';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [dynamicServices, setDynamicServices] = useState([]);
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();
  
  const isLoggingOut = useRef(false);
  const sessionCheckIntervalRef = useRef(null);

  // Fetch dynamic services from page_metadata (using page1, page2, page3 keys)
  const fetchDynamicServices = useCallback(async () => {
    try {
      setIsLoadingServices(true);
      const response = await axios.get('/api/page-metadata');
      
      // Map page1-page11 to services with their paths
      const serviceMapping = {
        'page1': { path: '/portfolio/registered-ra-platform' },
        'page2': { path: '/portfolio/ipo-research' },
        'page3': { path: '/portfolio/intraday-calls-dashboard' },
        'page4': { path: '/portfolio/heat-map-dashboard' },
        'page5': { path: '/portfolio/long-term-growth-portfolio' },
        'page6': { path: '/portfolio/fundamental-screener' },
        'page7': { path: '/portfolio/all-weather-portfolio' },
        'page8': { path: '/portfolio/dividend-growth-portfolio' },
        'page9': { path: '/portfolio/return-screener' },
        'page10': { path: '/portfolio/nifty-50' },
        'page11': { path: '/portfolio/premium-docs' }
      };
      
      // Filter only page1-page11 and map to services menu
      // Using 'title' from database for the display name
      // Filter out page11 (Premium Docs) from services dropdown
      const servicesMenu = response.data
        .filter(page => serviceMapping[page.page_key] && page.page_key !== 'page11') // Exclude page11 from services dropdown
        .map(page => ({
          id: page.id,
          name: page.title, // USING 'title' FROM DATABASE FOR SERVICE NAME
          originalPageName: page.page_name, // Keep original for reference
          path: serviceMapping[page.page_key].path,
          pageKey: page.page_key,
          description: page.description
        }))
        .sort((a, b) => {
          // Sort by page number (page1, page2, page3...)
          const aNum = parseInt(a.pageKey.replace('page', ''));
          const bNum = parseInt(b.pageKey.replace('page', ''));
          return aNum - bNum;
        });
      
      setDynamicServices(servicesMenu);
      console.log('Services loaded with dynamic titles:', servicesMenu);
      
    } catch (error) {
      console.error('Error fetching services:', error);
      // Fallback static data using page_name as fallback (exclude Premium Docs)
      setDynamicServices(getFallbackServices());
    } finally {
      setIsLoadingServices(false);
    }
  }, []);

  // Fallback static services (used only if API fails) - excluding Premium Docs
  const getFallbackServices = () => {
    return [
      { name: 'Registered RA Platform', path: '/portfolio/registered-ra-platform' },
      { name: 'IPO Research', path: '/portfolio/ipo-research' },
      { name: 'Intraday Calls Dashboard', path: '/portfolio/intraday-calls-dashboard' },
      { name: 'Heat Map Dashboard', path: '/portfolio/heat-map-dashboard' },
      { name: 'Long Term Growth', path: '/portfolio/long-term-growth-portfolio' },
      { name: 'Fundamental Screener', path: '/portfolio/fundamental-screener' },
      { name: 'All Weather Portfolio', path: '/portfolio/all-weather-portfolio' },
      { name: 'Dividend Growth', path: '/portfolio/dividend-growth-portfolio' },
      { name: 'Return Screener', path: '/portfolio/return-screener' },
      { name: 'Nifty 50', path: '/portfolio/nifty-50' }
      // Premium Docs removed from services dropdown
    ];
  };

  const isActivePath = (path) => {
    if (!path) return false;
    return location.pathname === path;
  };

  const isSubmenuActive = (submenu) => {
    if (!submenu) return false;
    return submenu.some(item => isActivePath(item.path));
  };

  const handleAutoLogout = useCallback((showAlert = true) => {
    if (isLoggingOut.current) return;
    isLoggingOut.current = true;
    
    if (showAlert && localStorage.getItem('accessToken')) {
      alert('You have been logged out from another device.');
    }
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('rememberMe');
    
    window.dispatchEvent(new Event('storage'));
    setUser(null);
    
    if (sessionCheckIntervalRef.current) {
      clearInterval(sessionCheckIntervalRef.current);
      sessionCheckIntervalRef.current = null;
    }
    
    setTimeout(() => {
      isLoggingOut.current = false;
    }, 1000);
    
    if (!location.pathname.includes('/login')) {
      navigate('/login');
    }
  }, [navigate, location]);

  // Axios interceptors
  useEffect(() => {
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          try {
            const refreshToken = localStorage.getItem('refreshToken');
            if (!refreshToken) throw new Error('No refresh token');
            
            const response = await axios.post('/api/auth/refresh-token', { refreshToken });
            
            if (response.data.success) {
              localStorage.setItem('accessToken', response.data.data.accessToken);
              originalRequest.headers.Authorization = `Bearer ${response.data.data.accessToken}`;
              return axios(originalRequest);
            }
          } catch (refreshError) {
            handleAutoLogout(false);
            return Promise.reject(refreshError);
          }
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
  }, [handleAutoLogout]);

  // Load user and fetch services
  useEffect(() => {
    const loadUser = () => {
      const storedUser = localStorage.getItem('user');
      setUser(storedUser ? JSON.parse(storedUser) : null);
    };

    loadUser();
    fetchDynamicServices();

    const handleStorageChange = (e) => {
      if (e.key === 'user' || e.key === 'accessToken') {
        loadUser();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchDynamicServices]);

  // Session check
  useEffect(() => {
    if (!user) return;

    const checkSession = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        if (!token) {
          handleAutoLogout(false);
          return;
        }

        const response = await axios.get('/api/auth/check-session', {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        if (!response.data.success || !response.data.data?.isLoggedIn || !response.data.data?.sessionValid) {
          handleAutoLogout();
        }
      } catch (error) {
        if (error.response?.status === 401) {
          handleAutoLogout();
        }
      }
    };

    sessionCheckIntervalRef.current = setInterval(checkSession, 300000);

    const handleVisibilityChange = () => {
      if (!document.hidden) checkSession();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', checkSession);
    checkSession();

    return () => {
      if (sessionCheckIntervalRef.current) clearInterval(sessionCheckIntervalRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', checkSession);
    };
  }, [user, handleAutoLogout]);

  // Route change session check
  useEffect(() => {
    if (user) {
      const checkSessionOnNavigate = async () => {
        try {
          const token = localStorage.getItem('accessToken');
          if (!token) {
            handleAutoLogout(false);
            return;
          }

          const response = await axios.get('/api/auth/check-session', {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          if (!response.data.success || !response.data.data?.isLoggedIn || !response.data.data?.sessionValid) {
            handleAutoLogout();
          }
        } catch (error) {
          if (error.response?.status === 401) handleAutoLogout();
        }
      };

      checkSessionOnNavigate();
    }
  }, [location.pathname, user, handleAutoLogout]);

  const handleNavigation = (item) => {
    if (item.scrollTo) {
      if (location.pathname !== '/') {
        navigate('/', { state: { scrollTo: item.scrollTo } });
      } else {
        const element = document.getElementById(item.scrollTo);
        if (element) element.scrollIntoView({ behavior: 'smooth' });
      }
    } else if (item.path) {
      navigate(item.path);
    }
    setIsMenuOpen(false);
  };

  // Get premium docs item from API or fallback
  const getPremiumDocsItem = useCallback(async () => {
    try {
      const response = await axios.get('/api/page-metadata');
      const premiumDocsPage = response.data.find(page => page.page_key === 'page11');
      if (premiumDocsPage) {
        return {
          name: premiumDocsPage.title,
          path: '/portfolio/premium-docs'
        };
      }
    } catch (error) {
      console.error('Error fetching premium docs:', error);
    }
    // Fallback
    return { name: 'Premium Docs', path: '/portfolio/premium-docs' };
  }, []);

  // Navigation items with dynamic services (using title from database)
  const [premiumDocsItem, setPremiumDocsItem] = useState(null);
  
  useEffect(() => {
    const loadPremiumDocs = async () => {
      const item = await getPremiumDocsItem();
      setPremiumDocsItem(item);
    };
    loadPremiumDocs();
  }, [getPremiumDocsItem]);

  // Build navItems without Premium Docs in services dropdown
  const navItems = [
    { name: 'Home', scrollTo: 'hero' },
    { name: 'About', scrollTo: 'about' },
    {
      name: 'Services',
      submenu: dynamicServices.length > 0 ? dynamicServices : getFallbackServices()
    },
    { name: 'Disclosures', scrollTo: 'disclosures' },
    { name: 'Member Benefits', scrollTo: 'member-benefits' },
    { name: 'Membership Guide', scrollTo: 'getting-started' },
  ];

  // Add Premium Docs as a separate main menu item if it exists
  if (premiumDocsItem) {
    navItems.push({ name: premiumDocsItem.name, path: premiumDocsItem.path });
  }
  
  // Add Contact at the end
  navItems.push({ name: 'Contact', scrollTo: 'contact' });

  const handleLogout = async () => {
    if (!user) return;

    if (window.confirm('Are you sure you want to logout?')) {
      try {
        const token = localStorage.getItem('accessToken');
        if (token) {
          await axios.post('/api/auth/logout', {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        }

        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tokenExpiry');
        localStorage.removeItem('rememberMe');

        window.dispatchEvent(new Event('storage'));
        setUser(null);
        navigate('/');
      } catch (err) {
        console.error('Logout failed', err);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        localStorage.removeItem('tokenExpiry');
        localStorage.removeItem('rememberMe');
        window.dispatchEvent(new Event('storage'));
        setUser(null);
        navigate('/');
      }
    }
  };

  // Loading state
  if (isLoadingServices && dynamicServices.length === 0) {
    return (
      <header className="sticky top-0 z-50 bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-2">
              <img src={logo} className='h-15' alt="Logo" style={{ height: '65px' }} height={65} width={65} />
            </Link>
            <div className="hidden md:flex items-center space-x-8">
              <div className="animate-pulse text-gray-500">Loading menu...</div>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="sticky top-0 z-50 bg-white shadow-md">
      <div className="max-w-9xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <img src={logo} className='h-15' alt="Logo" style={{ height: '65px' }} height={65} width={65} />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {navItems.map((item) => (
              <div key={item.name} className="relative group cursor-pointer">
                {item.submenu ? (
                  <div className={`flex items-center space-x-1 cursor-pointer transition-colors duration-300 ${
                    isSubmenuActive(item.submenu) 
                      ? 'text-blue-600 font-semibold' 
                      : 'text-gray-700 hover:text-blue-600 font-medium'
                  }`}>
                    <span>{item.name}</span>
                    <svg className={`w-3 h-3 ml-1 mt-1 transition-colors duration-300 ${
                      isSubmenuActive(item.submenu) ? 'text-blue-600' : 'text-gray-500 group-hover:text-blue-600'
                    }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                ) : (
                  <button onClick={() => handleNavigation(item)} className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-300 cursor-pointer">
                    {item.name}
                  </button>
                )}

                {item.submenu && (
                  <div className="absolute left-0 mt-2 w-70 bg-white shadow-lg rounded-md opacity-0 group-hover:opacity-100 invisible group-hover:visible transition-all duration-300 z-50">
                    {item.submenu.map((subItem) => (
                      <button
                        key={subItem.id || subItem.name}
                        onClick={() => handleNavigation(subItem)}
                        className={`block w-full text-left px-4 py-2 font-medium transition-colors duration-200 ${
                          isActivePath(subItem.path)
                            ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600'
                            : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                        }`}
                      >
                        {subItem.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/* User Profile / Login */}
            {user ? (
              <div className="relative">
                <button onClick={() => setIsProfileOpen(!isProfileOpen)} className="flex items-center space-x-2 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors duration-300">
                  <FaUserCircle className="text-blue-600 text-2xl" />
                  <div className="text-left">
                    <p className="text-sm font-semibold text-gray-900">{user.username}</p>
                    <p className="text-xs text-gray-500">{user.email}</p>
                  </div>
                </button>

                {isProfileOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                    <button onClick={handleLogout} className="flex items-center w-full px-4 py-3 text-red-600 hover:bg-gray-50">
                      <FaSignOutAlt className="mr-3" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white font-semibold rounded-lg">
                <FaSignInAlt />
                <span>Login</span>
              </Link>
            )}
          </nav>

          {/* Mobile menu button */}
          <button onClick={() => setIsMenuOpen(!isMenuOpen)} className="md:hidden p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-gray-100">
            {isMenuOpen ? <FaTimes className="h-6 w-6" /> : <FaBars className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200 shadow-lg">
            <div className="px-2 pt-2 pb-3 space-y-1">
              {navItems.map((item) => (
                item.submenu ? (
                  <div key={item.name} className="space-y-1">
                    <div className={`px-3 py-3 font-medium ${isSubmenuActive(item.submenu) ? 'text-blue-600' : 'text-gray-700'}`}>
                      {item.name}
                    </div>
                    {item.submenu.map((subItem) => (
                      <button key={subItem.id || subItem.name} onClick={() => handleNavigation(subItem)} className={`block w-full text-left px-6 py-2 rounded-md font-medium transition-colors duration-200 ${
                        isActivePath(subItem.path) ? 'bg-blue-50 text-blue-600 border-l-4 border-blue-600' : 'text-gray-600 hover:text-blue-600 hover:bg-gray-50'
                      }`}>
                        {subItem.name}
                      </button>
                    ))}
                  </div>
                ) : (
                  <button key={item.name} onClick={() => handleNavigation(item)} className="block w-full text-left px-3 py-3 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-md font-medium">
                    {item.name}
                  </button>
                )
              ))}

              {/* Mobile User Section */}
              {user ? (
                <div className="px-3 py-4 border-t border-gray-200">
                  <div className="flex items-center space-x-3 mb-4">
                    <FaUserCircle className="text-blue-600 text-3xl" />
                    <div>
                      <p className="font-semibold text-gray-900">{user.username}</p>
                      <p className="text-sm text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <button onClick={() => { setIsMenuOpen(false); handleLogout(); }} className="block w-full text-left px-4 py-3 text-red-600 hover:bg-gray-50 rounded-md font-medium">
                    Logout
                  </button>
                </div>
              ) : (
                <div className="px-3 py-4 border-t border-gray-200">
                  <Link to="/login" className="flex items-center justify-center space-x-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-600 text-white font-semibold rounded-lg hover:shadow-lg" onClick={() => setIsMenuOpen(false)}>
                    <FaSignInAlt />
                    <span>Login</span>
                  </Link>
                </div>
              )}

              {/* Mobile Contact Info */}
              <div className="px-3 py-4 space-y-4 border-t border-gray-200">
                <div className="flex items-center space-x-3">
                  <FaPhoneAlt className="text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-500">Call Us</p>
                    <p className="text-gray-900 font-semibold">+91 9319745997</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <FaEnvelope className="text-blue-600" />
                  <div>
                    <p className="text-sm text-gray-500">Email Us</p>
                    <p className="text-gray-900 font-semibold">contact@example.com</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Close dropdown when clicking outside */}
      {isProfileOpen && <div className="fixed inset-0 z-40" onClick={() => setIsProfileOpen(false)} />}
    </header>
  );
};

export default Header;